import { useEffect, useState } from "react";
import fortressLogo from "../assets/fortress safe png1.png";

const navItems = [
  {
    label: "Solutions",
    href: "/solutions",
    children: [
      ["Custody", "/solutions"],
      ["Automated access", "/access"],
      ["Insurance cover", "/insurance"],
      ["Security governance", "/security"],
      ["Asset tracking", "/tracking"],
      ["Invoice", "https://invoice.fortresssafelimited.com"],
      ["Case studies", "/case-studies"],
    ],
  },
  {
    label: "Hold",
    href: "/boxes",
    children: [
      ["Safe box sizes", "/boxes"],
      ["Pricing", "/pricing"],
      ["Payment policy", "/payment-policy"],
      ["FAQs", "/faqs"],
    ],
  },
  { label: "Access", href: "/access" },
  {
    label: "Clients",
    href: "/clients",
    children: [
      ["Individuals", "/clients"],
      ["Corporate", "/clients"],
      ["Global travellers", "/clients"],
      ["Legal firms & trustees", "/clients"],
      ["Testimonials", "/clients"],
    ],
  },
  {
    label: "About us",
    href: "/about",
    children: [
      ["Company", "/about"],
      ["Support", "/support"],
      ["Careers", "/careers"],
      ["Insights", "/insights"],
      ["Privacy policy", "/privacy-policy"],
    ],
  },
];

function SunIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2" />
      <path
        d="M12 2v3M12 19v3M4.93 4.93l2.12 2.12M16.95 16.95l2.12 2.12M2 12h3M19 12h3M4.93 19.07l2.12-2.12M16.95 7.05l2.12-2.12"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="2"
      />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20 14.4A7.7 7.7 0 0 1 9.6 4a8.2 8.2 0 1 0 10.4 10.4Z"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

function Header({
  currentPath,
  logo = fortressLogo,
  navItems: navigationItems = navItems,
  theme,
  toggleTheme,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [hasScrolled, setHasScrolled] = useState(false);

  const closeMenu = () => {
    setIsOpen(false);
    setActiveMenu(null);
  };

  useEffect(() => {
    const handleScroll = () => setHasScrolled(window.scrollY > 64);

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (item) =>
    currentPath === item.href ||
    item.children?.some(([, href]) => currentPath === href);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        hasScrolled
          ? "border-b border-ink/10 bg-white/[0.88] py-3 text-ink backdrop-blur-2xl dark:border-brand-gold/15 dark:bg-brand-blue/90 dark:text-white"
          : "bg-transparent py-5 text-ink dark:text-white"
      }`}
      onMouseLeave={() => setActiveMenu(null)}
    >
      <div className="mx-auto flex w-full max-w-[1440px] items-center justify-between gap-5 px-5 transition-all duration-300 sm:px-8 lg:px-10">
        <a
          aria-label="Fortress Safe home"
          className="flex h-14 w-[11rem] shrink-0 items-center justify-start overflow-visible bg-transparent transition-transform duration-200 hover:-translate-y-0.5 sm:h-16 sm:w-[12.5rem]"
          href="/"
          onClick={closeMenu}
        >
          <img
            className="h-16 w-auto max-w-full object-contain mix-blend-multiply transition duration-300 dark:brightness-0 dark:invert dark:mix-blend-normal sm:h-[5.6rem]"
            src={logo}
            alt="Fortress Safe"
            decoding="async"
          />
        </a>

        <nav className="hidden flex-1 items-center justify-end gap-5 text-[15px] font-normal xl:flex 2xl:gap-7">
          {navigationItems.map((item) => (
            <div
              className="relative"
              key={item.label}
              onMouseEnter={() => setActiveMenu(item.children ? item.label : null)}
              onFocus={() => setActiveMenu(item.children ? item.label : null)}
            >
              <a
                className={`group relative inline-flex min-h-9 items-center gap-1.5 px-1.5 transition-colors duration-150 hover:text-brand-blend dark:hover:text-brand-gold ${
                  isActive(item)
                    ? "text-brand-blend dark:text-brand-gold"
                    : "text-current/[0.78]"
                }`}
                href={item.href}
              >
                <span
                  className="absolute inset-x-0 -bottom-1 h-px origin-left scale-x-0 bg-brand-gold transition-transform duration-150 group-hover:scale-x-100"
                  aria-hidden="true"
                />

                <span className="relative">{item.label}</span>

                <span className="relative text-brand-gold" aria-hidden="true">
                  -&gt;
                </span>
              </a>

              {item.children ? (
                <div
                  className={`absolute left-1/2 top-full z-50 w-[34rem] -translate-x-1/2 pt-3 transition-all duration-300 ${
                    activeMenu === item.label
                      ? "pointer-events-auto translate-y-0 opacity-100"
                      : "pointer-events-none -translate-y-3 opacity-0"
                  }`}
                >
                  <div className="grid origin-top grid-cols-[0.9fr_1.1fr] gap-8 rounded-[20px] border border-brand-gold/20 bg-white/[0.94] p-7 text-ink ring-1 ring-white/70 backdrop-blur-2xl dark:border-brand-gold/20 dark:bg-brand-blue/[0.96] dark:text-white dark:ring-white/10">
                    <div>
                      <p className="mb-4">{item.label}</p>

                      <h3 className="text-2xl font-light leading-tight">
                        {item.label === "Solutions"
                          ? "Explore the complete Fortress custody stack."
                          : item.label === "Hold"
                            ? "Choose the storage model that fits your valuables."
                            : item.label === "Clients"
                              ? "Serve every client profile with the same controlled flow."
                              : "Learn more about Fortress Safe, careers, and client guidance."}
                      </h3>
                    </div>

                    <nav className="grid gap-2" aria-label={`${item.label} submenu`}>
                      {item.children.map(([label, href]) => (
                        <a
                          className="group flex items-center justify-between rounded-2xl border border-ink/[0.08] bg-white/[0.82] px-4 py-4 transition hover:translate-x-1 hover:border-brand-gold/45 hover:bg-brand-gold/10 focus:outline-none focus:ring-2 focus:ring-brand-gold/50 dark:border-pale/10 dark:bg-white/[0.05]"
                          href={href}
                          key={label}
                          onClick={() => setActiveMenu(null)}
                        >
                          <span>{label}</span>

                          <span
                            className="text-brand-blend transition group-hover:translate-x-1 dark:text-brand-gold"
                            aria-hidden="true"
                          >
                            -&gt;
                          </span>
                        </a>
                      ))}
                    </nav>
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </nav>

        <div className="hidden items-center gap-3 xl:flex">
          <div className="flex items-center gap-3 border-l border-current/15 pl-5 text-sm text-current/70">
            <a
              className="inline-flex min-h-9 items-center rounded-full border border-current/[0.14] px-4 text-sm font-medium text-current transition hover:-translate-y-0.5 hover:border-brand-gold hover:text-brand-blend dark:hover:text-brand-gold"
              href="/contact"
            >
              Contact
            </a>
          </div>

          <button
            aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            aria-pressed={theme === "dark"}
            className="relative h-9 w-16 rounded-full border border-current/15 bg-white/[0.45] p-1 transition hover:-translate-y-0.5 dark:bg-white/10"
            onClick={toggleTheme}
            type="button"
          >
            <span
              className={`grid h-7 w-7 place-items-center rounded-full bg-brand-gold text-[11px] font-bold text-brand-blue transition-transform duration-300 ${
                theme === "dark" ? "translate-x-7" : "translate-x-0"
              }`}
            >
              {theme === "dark" ? <MoonIcon /> : <SunIcon />}
            </span>
          </button>

          {/* <a
            className="inline-flex min-h-10 items-center px-4 py-3 text-sm font-medium text-current/75 transition hover:text-brand-blend dark:hover:text-brand-gold"
            href="/login"
          >
            Login
          </a>

          <a className="cc-button min-h-10 px-6 py-3 text-sm" href="/get-started">
            Get started
          </a> */}
        </div>

        <button
          aria-expanded={isOpen}
          aria-label="Toggle navigation"
          className="relative z-[100] grid h-11 w-11 place-items-center bg-transparent text-brand-gold transition duration-300 hover:text-brand-blend dark:text-brand-gold dark:hover:text-white xl:hidden"
          onClick={() => setIsOpen((value) => !value)}
          type="button"
        >
          <span className="relative h-5 w-6">
            <i
              className={`absolute left-0 top-1 block h-0.5 w-6 bg-current transition ${
                isOpen ? "translate-y-2 rotate-45" : ""
              }`}
            />
            <i
              className={`absolute left-0 top-3 block h-0.5 w-6 bg-current transition ${
                isOpen ? "-translate-y-0 rotate-[-45deg]" : ""
              }`}
            />
          </span>
        </button>
      </div>

      <div
        className={`mx-3 grid overflow-hidden rounded-[20px] border bg-brand-blue/[0.96] text-white backdrop-blur-2xl transition-[max-height,opacity,transform] duration-300 xl:hidden ${
          isOpen
            ? "mt-3 max-h-[calc(100vh-6rem)] translate-y-0 overflow-y-auto border-white/15 opacity-100"
            : "max-h-0 -translate-y-3 border-transparent opacity-0"
        }`}
      >
        <button
          className="mx-5 mt-5 inline-flex h-11 items-center justify-between rounded-full border border-white/15 px-4 text-sm"
          onClick={toggleTheme}
          type="button"
        >
          <span>{theme === "dark" ? "Dark mode" : "Light mode"}</span>

          <span className="grid h-8 w-8 place-items-center rounded-full bg-brand-gold text-brand-blue">
            {theme === "dark" ? <MoonIcon /> : <SunIcon />}
          </span>
        </button>

        <div className="grid py-4">
          {navigationItems.map((item) => (
            <div key={item.label}>
              <a
                className="block border-b border-white/10 px-6 py-4 text-lg font-normal transition hover:bg-white/[0.04]"
                href={item.href}
                onClick={closeMenu}
              >
                {item.label}
              </a>

              {item.children ? (
                <div className="grid bg-white/[0.04]">
                  {item.children.map(([label, href]) => (
                    <a
                      className="border-b border-white/10 px-9 py-3 text-sm text-white/[0.72] transition hover:text-brand-gold"
                      href={href}
                      key={label}
                      onClick={closeMenu}
                    >
                      {label}
                    </a>
                  ))}
                </div>
              ) : null}
            </div>
          ))}

          <div className="mx-5 mt-4 flex flex-wrap items-center gap-3 text-sm text-white/70">
            <a
              className="inline-flex min-h-9 items-center rounded-full border border-white/[0.12] px-4 transition hover:border-brand-gold hover:text-brand-gold"
              href="/contact"
              onClick={closeMenu}
            >
              Contact
            </a>
          </div>

          <div className="mx-5 mb-5 mt-5 grid gap-3">
            <a
              className="inline-flex min-h-11 items-center justify-center border border-white/[0.12] px-5 py-3 text-center text-sm font-medium text-white transition hover:border-brand-gold hover:text-brand-gold"
              href="/login"
              onClick={closeMenu}
            >
              Login
            </a>

            <a
              className="cc-button px-5 py-3 text-center"
              href="/get-started"
              onClick={closeMenu}
            >
              Get started
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
